/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { Group } from "@dashboard-v3/api";
import { Box, CircularProgress, TextField } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { fetchGroups } from "utils/api/groups";
import throttle from "utils/throttle";

interface Props {
  value: Group[];
  onChange: (groupIds: Group[]) => void;
}

type SelectorState = {
  loading: boolean;
  inputValue: string;
  options: Group[];
  selectedGroups: Group[];
};

export default function ApprovalGroupSelector(props: Props) {
  const { t } = useTranslation("accounts");

  const { enqueueSnackbar } = useSnackbar();
  const [state, setState] = useState<SelectorState>(initState(props));

  function updateState(update: Partial<SelectorState>) {
    setState(prev => ({ ...prev, ...update }));
  }

  useEffect(() => {
    async function searchGroups() {
      throttle(async () => {
        updateState({ loading: true });
        try {
          const options = await fetchGroups(100000, 0, state.inputValue);
          updateState({ options });
        } catch (e) {
          enqueueSnackbar(t("approvalGroup.searchError"), { variant: "error" });
        } finally {
          updateState({ loading: false });
        }
      }, 200);
    }

    if (state.inputValue.trim()) searchGroups();
  }, [state.inputValue]);

  const getVisibleOption = (o: Group) => {
    return Boolean(o.name) ? o.name : o.email;
  };

  const onGroupSelect = (_, value) => {
    const groups = value as Group[];
    updateState({ selectedGroups: groups });
    props.onChange(groups);
  };

  return (
    <Box mb="20px">
      <Autocomplete
        multiple
        options={state.options}
        onChange={onGroupSelect}
        value={state.selectedGroups}
        getOptionLabel={getVisibleOption}
        renderOption={getVisibleOption}
        renderInput={props => (
          <TextField
            {...props}
            fullWidth
            variant="outlined"
            label={t("approvalGroups.label")}
            value={state.inputValue}
            onChange={e => updateState({ inputValue: e.target.value })}
            InputProps={{
              ...props.InputProps,
              endAdornment: (
                <>
                  {state.loading && (
                    <CircularProgress color="primary" size={20} />
                  )}
                  {props.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Box>
  );
}

function initState(props: Props) {
  return () => ({
    inputValue: "",
    loading: false,
    options: [],
    selectedGroups: props.value,
  });
}
